import { Component, OnInit } from '@angular/core';
import { AdminAuthService } from '../../services/admin-auth.service';
import { AdminApiService, JobApplication, JobListingFilters, JobListingResponse } from '../../services/admin-api.service';

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss']
})
export class AdminDashboardComponent implements OnInit {

  applications: JobApplication[] = [];
  isLoading = false;
  errorMessage = '';
  
  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  limit = 10;

  // Filters
  filters: JobListingFilters = {
    page: 1,
    limit: 10
  };

  searchText = '';
  selectedCategory = '';
  selectedJobType = '';
  selectedCityCode = '';
  selectedOnlineApplication = '';

  // Filter options
  jobTypes = ['Full Time', 'Part Time', 'Contract', 'Internship'];
  
  adminEmail = '';

  // Modal
  showModal = false;
  selectedApplication: JobApplication | null = null;

  constructor(
    private adminAuthService: AdminAuthService,
    private adminApiService: AdminApiService
  ) {
    const user = this.adminAuthService.getAdminUser();
    this.adminEmail = user?.email || '';
  }

  ngOnInit(): void {
    this.loadApplications();
  }

  loadApplications(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const filters: JobListingFilters = {
      page: this.currentPage,
      limit: this.limit
    };

    if (this.searchText.trim()) {
      filters.search = this.searchText.trim();
    }
    if (this.selectedCategory.trim()) {
      filters.category = this.selectedCategory.trim();
    }
    if (this.selectedJobType) {
      filters.job_type = this.selectedJobType;
    }
    if (this.selectedCityCode.trim()) {
      filters.city_code = this.selectedCityCode.trim();
    }
    if (this.selectedOnlineApplication !== '') {
      filters.is_online_application = this.selectedOnlineApplication === 'true';
    }

    this.adminApiService.getJobApplications(filters).subscribe({
      next: (response: JobListingResponse) => {
        this.applications = response.items;
        this.totalPages = response.pages;
        this.totalItems = response.total;
        this.currentPage = response.page;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = 'Failed to load applications. Please try again.';
        this.isLoading = false;
        console.error('Error loading applications:', err);
      }
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadApplications();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadApplications();
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedCategory = '';
    this.selectedJobType = '';
    this.selectedCityCode = '';
    this.selectedOnlineApplication = '';
    this.currentPage = 1;
    this.loadApplications();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadApplications();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  exportCSV(): void {
    this.adminApiService.exportApplications();
  }

  logout(): void {
    this.adminAuthService.logout();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatSalary(compensation: JobApplication['job']['compensation'] | undefined): string {
    if (!compensation?.range) return 'N/A';
    const { min, max } = compensation.range;
    const currency = compensation.currency || 'INR';
    return `${currency} ${min?.toLocaleString() || 0} - ${max?.toLocaleString() || 0}`;
  }

  getUserName(user: JobApplication['user'] | undefined): string {
    if (!user) return 'Unknown';
    return user.name || user.email || 'Unknown';
  }

  getUserContact(user: JobApplication['user'] | undefined): string {
    if (!user) return 'N/A';
    const parts = [];
    if (user.email) parts.push(user.email);
    if (user.phone) parts.push(user.phone);
    return parts.join(' | ') || 'N/A';
  }

  getSkills(user: JobApplication['user'] | undefined): string {
    if (!user?.skills?.length) return 'N/A';
    return user.skills.map(s => s?.name || '').filter(n => n).join(', ') || 'N/A';
  }

  getLanguages(user: JobApplication['user'] | undefined): string {
    if (!user?.languages?.length) return 'N/A';
    return user.languages.map(l => l?.name || '').filter(n => n).join(', ') || 'N/A';
  }

  viewApplication(app: JobApplication): void {
    this.selectedApplication = app;
    this.showModal = true;
    document.body.style.overflow = 'hidden';
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedApplication = null;
    document.body.style.overflow = '';
  }
}

